import React, { useState, useMemo, useEffect } from 'react';
import Button from '../../UI/Button';
import currencyService from '../../../services/currency/currencyService';
import { formatShopifyPriceForDisplay } from '../../../utils/priceFormat';
import { formatDateTimeIstanbul } from '../../../utils/dateTimeTr';
import './ProductList.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

const ProductList = ({
  products,
  selectedIds,
  onToggleSelect,
  onSetSelectedIds,
  onEditProduct,
  onEditStock,
  loading = false
}) => {
  // Sıralama state'i
  const [sort, setSort] = useState({ field: null, direction: 'asc' });

  // Pagination state'i
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // EUR kuru state'i - header ile aynı servisi kullanır, cache'ten okur
  const [eurRate, setEurRate] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchRate = async () => {
      try {
        const rates = await currencyService.getCurrencyRates();
        if (!cancelled && rates?.EUR?.buying) {
          setEurRate(rates.EUR.buying);
        }
      } catch (err) {
        // Kur alınamadıysa sessizce geç; kolon "-" gösterecek
        console.warn('ProductList: EUR kuru alınamadı', err.message);
      }
    };

    fetchRate();

    // Her 5 dakikada bir güncelle (header ile aynı ritim)
    const interval = setInterval(fetchRate, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // TL fiyatı EUR'a çevir
  const toEuro = (tlPrice) => {
    if (!eurRate) return null;
    const tl = parseFloat(tlPrice);
    if (isNaN(tl)) return null;
    return tl / eurRate;
  };

  // Sütuna tıklandığında: aynı sütun → yön değiştir, farklı sütun → asc'ten başla
  const handleSort = (field) => {
    setSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'asc' };
    });
  };

  // Sıralanmış ürünler
  const sortedProducts = useMemo(() => {
    if (!sort.field) return products;

    const sorted = [...products].sort((a, b) => {
      if (sort.field === 'title') {
        const aVal = (a.title || '').toLowerCase();
        const bVal = (b.title || '').toLowerCase();
        return aVal.localeCompare(bVal, 'tr');
      }

      if (sort.field === 'price') {
        const aVal = parseFloat(a.variants?.[0]?.price) || 0;
        const bVal = parseFloat(b.variants?.[0]?.price) || 0;
        return aVal - bVal;
      }

      if (sort.field === 'stock') {
        const aVal = parseInt(a.variants?.[0]?.inventory_quantity, 10);
        const bVal = parseInt(b.variants?.[0]?.inventory_quantity, 10);
        const aNum = Number.isNaN(aVal) ? -1 : aVal;
        const bNum = Number.isNaN(bVal) ? -1 : bVal;
        return aNum - bNum;
      }

      if (sort.field === 'lastPriceChange') {
        const aTime = a.priceChangeMeta?.changedAt
          ? new Date(a.priceChangeMeta.changedAt).getTime()
          : null;
        const bTime = b.priceChangeMeta?.changedAt
          ? new Date(b.priceChangeMeta.changedAt).getTime()
          : null;
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return 1;
        if (bTime === null) return -1;
        return aTime - bTime;
      }

      return 0;
    });

    return sort.direction === 'desc' ? sorted.reverse() : sorted;
  }, [products, sort]);

  // Pagination hesaplamaları
  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / pageSize));

  // Sıralama veya sayfa boyutu değişince sayfa 1'e dön
  useEffect(() => {
    setCurrentPage(1);
  }, [sort, pageSize, products.length]);

  // Sayfa sınırları dışındaysa düzelt
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Görüntülenecek ürünler (sayfalama uygulanmış)
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedProducts.slice(start, start + pageSize);
  }, [sortedProducts, currentPage, pageSize]);

  // Görünür sayfa numaraları (... ile kısaltılmış)
  const visiblePageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      // Az sayfa varsa hepsini göster
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    // Her zaman ilk sayfa
    pages.push(1);

    // Aralıktaki sayfalar
    let rangeStart = Math.max(2, currentPage - 1);
    let rangeEnd = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      rangeEnd = 4;
    } else if (currentPage >= totalPages - 2) {
      rangeStart = totalPages - 3;
    }

    if (rangeStart > 2) pages.push('...');

    for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);

    if (rangeEnd < totalPages - 1) pages.push('...');

    // Her zaman son sayfa
    pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  const SortIcon = ({ field }) => {
    const isActive = sort.field === field;
    if (!isActive) {
      return <span className="sort-icon sort-icon-inactive">⇅</span>;
    }
    return (
      <span className="sort-icon sort-icon-active">
        {sort.direction === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="product-list-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Ürünler yükleniyor...</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="product-list-empty">
        <div className="empty-icon">📦</div>
        <h3>Ürün Bulunamadı</h3>
        <p>Henüz hiç ürün yok veya arama kriterlerinize uygun ürün bulunamadı.</p>
      </div>
    );
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, sortedProducts.length);

  // Tüm ürünleri seç (sayfa farketmez - sıralanmış ve filtrelenmiş tüm ürünler)
  const handleSelectAll = () => {
    if (!onSetSelectedIds) return;
    onSetSelectedIds(sortedProducts.map(p => p.id));
  };

  // Seçimi tamamen temizle
  const handleClearSelection = () => {
    if (!onSetSelectedIds) return;
    onSetSelectedIds([]);
  };

  const paginationControls = (
    <div className="pagination-container">
      <div className="pagination-info">
        <span className="pagination-range">
          <strong>{startItem}-{endItem}</strong> / {sortedProducts.length} ürün
        </span>

        <div className="page-size-selector">
          <label htmlFor="page-size">Sayfa başına:</label>
          <select
            id="page-size"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="page-size-select"
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>

        <div className="selection-buttons">
          <button
            type="button"
            className="selection-btn selection-btn-primary"
            onClick={handleSelectAll}
            disabled={!onSetSelectedIds || sortedProducts.length === 0 || selectedIds.length === sortedProducts.length}
            title="Tüm sayfalardaki ürünleri seç"
          >
            Tüm Ürünleri Seç
          </button>
          <button
            type="button"
            className="selection-btn selection-btn-secondary"
            onClick={handleClearSelection}
            disabled={!onSetSelectedIds || selectedIds.length === 0}
            title="Tüm seçimleri kaldır"
          >
            Seçimi Temizle
          </button>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            title="İlk sayfa"
          >
            «
          </button>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            title="Önceki sayfa"
          >
            ‹
          </button>

          {visiblePageNumbers.map((page, idx) => (
            page === '...' ? (
              <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
            ) : (
              <button
                key={page}
                className={`pagination-btn pagination-page ${currentPage === page ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            )
          ))}

          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            title="Sonraki sayfa"
          >
            ›
          </button>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            title="Son sayfa"
          >
            »
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="product-list-container">
      {paginationControls}
      <div className="product-table-wrapper">
        <table className="product-table">
          <thead>
            <tr>
              <th className="select-column">
                <input
                  type="checkbox"
                  checked={
                    paginatedProducts.length > 0 &&
                    paginatedProducts.every(p => selectedIds.includes(p.id))
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      paginatedProducts.forEach(product => {
                        if (!selectedIds.includes(product.id)) {
                          onToggleSelect(product.id);
                        }
                      });
                    } else {
                      paginatedProducts.forEach(product => {
                        if (selectedIds.includes(product.id)) {
                          onToggleSelect(product.id);
                        }
                      });
                    }
                  }}
                  className="select-all-checkbox"
                  title="Bu sayfadaki tüm ürünleri seç/bırak"
                />
              </th>
              <th
                className="title-column sortable-column"
                onClick={() => handleSort('title')}
                title="Sıralamak için tıklayın"
              >
                <span className="sortable-header-content">
                  Ürün Başlığı
                  <SortIcon field="title" />
                </span>
              </th>
              <th
                className="price-column sortable-column"
                onClick={() => handleSort('price')}
                title="Sıralamak için tıklayın"
              >
                <span className="sortable-header-content sortable-header-right">
                  Fiyat (TL)
                  <SortIcon field="price" />
                </span>
              </th>
              <th
                className="price-column sortable-column"
                onClick={() => handleSort('price')}
                title="Sıralamak için tıklayın (TL ile aynı sıra)"
              >
                <span className="sortable-header-content sortable-header-right">
                  Fiyat (Euro)
                  <SortIcon field="price" />
                </span>
              </th>
              <th
                className="last-change-column sortable-column"
                onClick={() => handleSort('lastPriceChange')}
                title="Sıralamak için tıklayın"
              >
                <span className="sortable-header-content sortable-header-right">
                  Son Fiyat Değişimi
                  <SortIcon field="lastPriceChange" />
                </span>
              </th>
              <th className="old-price-column">Eski Fiyat (TL)</th>
              <th
                className="stock-column sortable-column"
                onClick={() => handleSort('stock')}
                title="Sıralamak için tıklayın"
              >
                <span className="sortable-header-content sortable-header-right">
                  Stok
                  <SortIcon field="stock" />
                </span>
              </th>
              <th className="action-column">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts.map((product) => (
              <tr
                key={product.id}
                className={`product-row ${selectedIds.includes(product.id) ? 'selected' : ''}`}
              >
                <td className="select-cell">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(product.id)}
                    onChange={() => onToggleSelect(product.id)}
                    className="product-checkbox"
                  />
                </td>
                <td className="title-cell">
                  <div className="product-title">
                    {product.title}
                  </div>
                  {product.variants?.[0]?.sku && (
                    <div className="product-sku">
                      SKU: {product.variants[0].sku}
                    </div>
                  )}
                </td>
                <td className="price-cell">
                  <span className="price-value">
                    {product.variants?.[0]?.price != null
                      ? `${formatShopifyPriceForDisplay(product.variants[0].price)} ₺`
                      : '—'}
                  </span>
                </td>
                <td className="price-cell">
                  <span className="price-value price-value-eur">
                    {(() => {
                      const eur = toEuro(product.variants?.[0]?.price);
                      return eur !== null
                        ? `${formatShopifyPriceForDisplay(eur.toFixed(2))} €`
                        : '—';
                    })()}
                  </span>
                </td>
                <td className="last-change-cell">
                  <span className="last-change-value">
                    {formatDateTimeIstanbul(product.priceChangeMeta?.changedAt)}
                  </span>
                </td>
                <td className="old-price-cell">
                  <span className="old-price-value">
                    {product.priceChangeMeta?.previousPrice != null
                      ? `${formatShopifyPriceForDisplay(product.priceChangeMeta.previousPrice)} ₺`
                      : '—'}
                  </span>
                </td>
                <td className="stock-cell">
                  <span className="stock-value">
                    {product.variants?.[0]?.inventory_management
                      ? (product.variants[0].inventory_quantity ?? '—')
                      : 'Takip yok'}
                  </span>
                </td>
                <td className="action-cell">
                  <div className="action-buttons">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onEditProduct(product)}
                    >
                      Fiyat
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => onEditStock?.(product)}
                    >
                      Stok
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductList;
